/**
 * MCQ Element Player
 * Supports multiple content types for question and choices
 */

import * as React from 'react';
import { cn, Checkbox, shuffleArray } from '@ai-content-hub/content-kit';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import { Check, X } from 'lucide-react';
import {
    type MCQElementData,
    type MCQAnswer,
    type MCQChoice,
    getDisplayContent,
    getChoiceText,
    mcqElementSchema,
} from './schema';
import { BlockRenderer, type BlockRendererTextSize } from '../block';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

export type StandardOrAdaptive = 'standard' | 'adaptive';

export type MCQAnswerChoicesPresentation = StandardOrAdaptive;

const LG_MCQ_GRID_COLS = [
    '',
    'lg:grid-cols-1',
    'lg:grid-cols-2',
    'lg:grid-cols-3',
    'lg:grid-cols-4',
] as const;

function resolveAnswerChoicesPresentation(
    prop: MCQAnswerChoicesPresentation | undefined,
    config: ElementPlayerProps<MCQElementData, MCQAnswer>['config'] | undefined
): MCQAnswerChoicesPresentation {
    if (prop !== undefined) {
        return prop;
    }
    const b = config?.behavior;
    if (b !== undefined && typeof b === 'object' && !Array.isArray(b)) {
        const raw = (b as Record<string, unknown>).responsiveMcqAnswerChoices;
        if (raw === true) {
            return 'adaptive';
        }
    }
    return 'standard';
}

export interface MCQPlayerProps extends ElementPlayerProps<MCQElementData, MCQAnswer> {
    /**
     * Choice layout: `standard` = always vertical stack (default). `adaptive` = on large
     * viewports, a horizontal grid when there are fewer than five choices (opt-in responsive).
     * When omitted, you can still enable adaptive layout via
     * `config.behavior.responsiveMcqAnswerChoices === true`.
     */
    answerChoicesPresentation?: MCQAnswerChoicesPresentation;
}

export const MCQPlayer: React.FC<MCQPlayerProps> = ({
    data,
    answer,
    onAnswer,
    disabled = false,
    showFeedback = false,
    result,
    isReview = false,
    className,
    answerChoicesPresentation,
    config,
    elementChildLabels,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? mcqElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const presentation = React.useMemo(
        () => resolveAnswerChoicesPresentation(answerChoicesPresentation, config),
        [answerChoicesPresentation, config]
    );

    const choicesRaw = data?.choices;
    const shuffleChoices = data?.shuffleChoices ?? false;

    // ELMS-2370: hide answer => options also stay shuffled in review (authored order often leaks the key).
    const revealCorrect = config?.behavior?.showCorrectAnswer !== false;
    const [displayChoices, setDisplayChoices] = React.useState<MCQChoice[]>([]);

    React.useEffect(() => {
        const safeChoices = Array.isArray(choicesRaw) ? choicesRaw : [];
        if (shuffleChoices && (!isReview || !revealCorrect)) {
            setDisplayChoices(shuffleArray([...safeChoices]));
        } else {
            setDisplayChoices(safeChoices);
        }
    }, [choicesRaw, shuffleChoices, isReview, revealCorrect]);

    // ELMS-2370: LMS "hide answer" => config.behavior.showCorrectAnswer === false.
    // Never reveal the correct choice / explanation / per-choice feedback text; the
    // learner's own choice + right/wrong mark (existing behaviour) is kept.
    // ELMS-2370 (follow-up): hide answer also drops the right/wrong marks entirely —
    // the learner's own selection is shown neutrally (selected state).
    const graded = showFeedback && !!result && revealCorrect;

    const selectedIds = React.useMemo(() => {
        if (!answer) return [];
        return Array.isArray(answer) ? answer : [answer];
    }, [answer]);

    if (!data) {
        return (
            <div
                className={cn(
                    'mcq-player mcq-player--empty text-muted-foreground italic p-4',
                    className
                )}
            >
                {t('mcqPlayerNoData')}
            </div>
        );
    }

    const {
        question,
        multipleCorrect = false,
        explanation,
    } = data;

    const displayQuestionContent = question || {
        type: 'text' as const,
        text: '',
        variant: 'body' as const,
        alignment: 'left' as const,
    };

    const stemLabel = elementChildLabels?.mcqStem;

    const questionBlock = (textSize: BlockRendererTextSize | undefined) => (
        <div
            className={cn(
                'mcq-player-question text-lg font-medium',
                'lg:w-full lg:max-w-4xl',
                stemLabel != null && 'flex gap-2 items-start'
            )}
        >
            {stemLabel != null ? (
                <span
                    className="mcq-player-child-label mt-0.5 flex h-7 min-w-7 shrink-0 items-center justify-center rounded-md bg-muted-foreground/15 text-sm font-bold text-foreground"
                    aria-hidden
                >
                    {stemLabel}
                </span>
            ) : null}
            <div className="min-w-0 flex-1 [&_.tiptap-content]:bg-transparent">
                <BlockRenderer content={displayQuestionContent} textSize={textSize} />
            </div>
        </div>
    );

    const handleSelect = (choiceId: string) => {
        if (disabled || isReview) return;

        if (multipleCorrect) {
            // Toggle selection for multiple correct
            const newSelection = selectedIds.includes(choiceId)
                ? selectedIds.filter((id) => id !== choiceId)
                : [...selectedIds, choiceId];
            onAnswer?.(newSelection);
        } else {
            // Single selection
            onAnswer?.(choiceId);
        }
    };

    // Check if a choice is selected
    const isSelected = (choiceId: string) => selectedIds.includes(choiceId);

    // Dynamic text size for choice content on lg (horizontal layout): short text = larger font
    const getChoiceTextSize = (choice: MCQChoice): BlockRendererTextSize | undefined => {
        if (displayChoices.length >= 5) return undefined;
        const len = getChoiceText(choice).length;
        if (len < 15) return 'xl';
        if (len < 35) return 'lg';
        if (len < 70) return 'base';
        return 'sm';
    };

    // Get choice feedback after grading
    const getChoiceFeedback = (choice: MCQChoice) => {
        if (!showFeedback || !result || !revealCorrect) return null;

        const isChosen = isSelected(choice.id);

        if (isChosen && choice.feedback) {
            return choice.feedback;
        }

        return null;
    };

    // Get choice state classes
    // EAA-1680: border-primary/border-success/border-destructive here are also
    // read by skin-playful.css's .content-option state-shade selectors — keep
    // those class names in sync if this helper is ever refactored (same
    // keep-in-sync contract button.tsx documents for its own variant classes).
    const getChoiceClasses = (choice: MCQChoice) => {
        const isChosen = isSelected(choice.id);
        const isCorrectChoice = choice.isCorrect;

        if (graded) {
            if (isChosen && isCorrectChoice) {
                return 'border-success bg-success/10';
            }
            if (isChosen && !isCorrectChoice) {
                return 'border-destructive bg-destructive/10';
            }
            if (!isChosen && isCorrectChoice && isReview && revealCorrect) {
                return 'border-success/50 bg-success/5';
            }
        }

        if (isChosen) {
            return 'border-primary bg-primary/5';
        }

        return 'hover:border-primary/50 hover:bg-muted/50';
    };

    // EAA-1680: playful-skin pressable-option hooks (content-option /
    // content-option--static / content-option--selected /
    // content-feedback--correct|--incorrect) stamped alongside the state
    // utility classes above — see docs/PLAN-Playful-Interactive-Options.md.
    const getChoicePlayfulClasses = (choice: MCQChoice) => {
        const isChosen = isSelected(choice.id);
        const isCorrectChoice = choice.isCorrect;
        const isGraded = graded;
        const isStatic = disabled || isReview;

        return cn(
            'content-option',
            isStatic && 'content-option--static',
            isChosen && !isGraded && 'content-option--selected',
            isGraded && isChosen && isCorrectChoice && 'content-feedback--correct',
            isGraded && isChosen && !isCorrectChoice && 'content-feedback--incorrect'
        );
    };

    // No choices available
    if (displayChoices.length === 0) {
        return (
            <div
                className={cn(
                    'mcq-player mcq-player-root mcq-player--no-choices space-y-4',
                    className
                )}
            >
                {questionBlock(undefined)}
                <div className="mcq-player-no-choices text-muted-foreground italic">
                    {t('mcqPlayerNoChoices')}
                </div>
            </div>
        );
    }

    const isResponsiveChoiceLayout = presentation === 'adaptive' && displayChoices.length < 5;
    const lgGridColsClass = LG_MCQ_GRID_COLS[Math.min(displayChoices.length, 4)];

    return (
        <div
            className={cn(
                'mcq-player mcq-player-root space-y-4',
                isResponsiveChoiceLayout ? 'mcq-player--choices-responsive' : 'mcq-player--choices-vertical',
                className
            )}
        >
            {/* Question */}
            {questionBlock('lg')}

            {/* Choices: adaptive uses horizontal grid on lg when fewer than 5; standard is always vertical */}
            <div
                className={cn(
                    'mcq-player-choices gap-2 w-full px-2',
                    isResponsiveChoiceLayout
                        ? cn('grid grid-cols-1 lg:items-start', lgGridColsClass)
                        : 'flex flex-col'
                )}
            >
                {displayChoices.map((choice) => {
                    if (!choice || !choice.id) return null;

                    const isChosen = isSelected(choice.id);
                    const isCorrectChoice = choice.isCorrect;
                    const choiceFeedback = getChoiceFeedback(choice);

                    return (
                        <div
                            key={choice.id}
                            className={cn(
                                'mcq-player-choice-wrapper w-full',
                                isResponsiveChoiceLayout && 'min-h-0'
                            )}
                        >
                            {/*
                                EAA-1677: plain div, not the kit Card — repeating rows must use
                                `{type}-player-*` semantic classes, not per-row Card stacks (see
                                CLAUDE.md Styling → Content-type players). Also keeps this row out
                                of the playful skin's `[data-slot='card']` raised-surface styling,
                                which is meant for standalone surfaces, not option rows.
                            */}
                            <div
                                className={cn(
                                    'flex flex-col rounded-xl border bg-card text-card-foreground',
                                    'mcq-player-choice',
                                    'cursor-pointer transition-all duration-200',
                                    'p-0 h-auto w-full min-h-[44px]',
                                    getChoiceClasses(choice),
                                    getChoicePlayfulClasses(choice),
                                    (disabled || isReview) && 'cursor-default'
                                )}
                                onClick={() => handleSelect(choice.id)}
                            >
                                <div className="mcq-player-choice-body relative flex flex-row items-center gap-3 py-3 px-4 sm:py-4 [&:last-child]:pb-3">
                                    {/*
                                        Selection Indicator - in the flex row (shrink-0, vertically centred),
                                        never absolutely positioned outside the card: it used to sit at
                                        -8px/-8px and overlapped the card's top-left border. The whole card
                                        is the click target (onClick above), so the touch area stays >= 44px.
                                    */}
                                    <span className="mcq-player-choice-indicator pointer-events-none inline-flex size-5 shrink-0 items-center justify-center">
                                        {multipleCorrect ? (
                                            <Checkbox
                                                checked={isChosen}
                                                disabled={disabled || isReview}
                                                className={cn(
                                                    'size-5 bg-white',
                                                    graded &&
                                                        isChosen &&
                                                        !isCorrectChoice &&
                                                        'border-destructive'
                                                )}
                                            />
                                        ) : (
                                            // EAA-1679: kept as a plain div (not the kit RadioGroupItem) — Radix's
                                            // RadioGroupItem requires a RadioGroup context/value model and silently
                                            // drops custom children (its own Indicator+CircleIcon always wins over
                                            // passed children), which would lose the success/destructive Check/X
                                            // icons below. Stamping data-slot/data-state directly is the same
                                            // "hook into the skin cascade without adopting the component" pattern
                                            // ContentWrapper.tsx already uses for data-skin — skin-playful.css's
                                            // radio-group-item rule only ever reads these two attributes.
                                            <div
                                                data-slot="radio-group-item"
                                                data-state={isChosen ? 'checked' : 'unchecked'}
                                                className={cn(
                                                    'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors bg-white',
                                                    isChosen
                                                        ? graded
                                                            ? isCorrectChoice
                                                                ? 'border-success bg-success text-white'
                                                                : 'border-destructive bg-destructive text-white'
                                                            : 'border-primary bg-primary text-white'
                                                        : 'border-muted-foreground'
                                                )}
                                            >
                                                {isChosen &&
                                                    graded &&
                                                    (isCorrectChoice ? (
                                                        <Check className="w-3 h-3" />
                                                    ) : (
                                                        <X className="w-3 h-3" />
                                                    ))}
                                            </div>
                                        )}
                                    </span>

                                    {/* Choice Content - takes the remaining row width next to the indicator */}
                                    <div className="mcq-player-choice-content flex flex-1 min-w-0 overflow-hidden">
                                        {/*
                                            EAA-1680: rich-text choice content renders via RichTextViewer, whose
                                            `.tiptap-content` root paints `--color-surface` (tiptap.css). Inside an
                                            option row that surface shows as a white box over the row's own
                                            state background (bg-primary/5, bg-success/10, ...), so force it
                                            transparent here and let the option row own the background.
                                        */}
                                        {/*
                                            EAA-1772: cap answer-option media height. BlockRenderer's
                                            mediaMaxHeight (200) only constrains top-level image blocks,
                                            NOT images inside a rich-text choice (Moodle imports put the
                                            option image inline in rich text) — those rendered full size
                                            and blew the option up to fill the viewport. Cap any <img>
                                            here so inline rich-text images stay within the option.
                                        */}
                                        <div className="mcq-player-choice-text min-w-0 w-full flex flex-col [&_.tiptap-content]:bg-transparent [&_.block-renderer]:break-words [&_.block-renderer_*]:break-words [&_.block-renderer]:overflow-hidden [&_.block-renderer]:max-w-full [&_img]:max-h-[200px] [&_img]:w-auto [&_img]:object-contain">
                                            <div
                                                className={cn(
                                                    'flex w-full',
                                                    isResponsiveChoiceLayout
                                                        ? 'min-h-0 items-center justify-center lg:[&_.block-renderer]:!text-center lg:[&_.block-renderer_*]:!text-center lg:[&_.block-renderer]:shrink-0 lg:[&_.block-renderer]:max-w-full lg:[&_.image-element]:!mx-auto'
                                                        : 'items-center justify-start'
                                                )}
                                            >
                                                <BlockRenderer
                                                    content={getDisplayContent(
                                                        choice.content,
                                                        getChoiceText(choice)
                                                    )}
                                                    textSize={
                                                        presentation === 'adaptive'
                                                            ? getChoiceTextSize(choice)
                                                            : undefined
                                                    }
                                                    mediaMaxHeight={200}
                                                    mediaMaxWidth={200}
                                                    className={
                                                        isResponsiveChoiceLayout
                                                            ? 'lg:w-fit lg:shrink-0 lg:max-w-full'
                                                            : undefined
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Result Indicator - in the row after the content (never overlaps the text) */}
                                    {showFeedback &&
                                        result &&
                                        isReview &&
                                        revealCorrect &&
                                        isCorrectChoice &&
                                        !isChosen && (
                                            <span className="mcq-player-choice-correct-hint min-w-0 max-w-[40%] self-center break-words text-right text-xs text-success font-medium pointer-events-none lg:max-w-none lg:shrink-0 lg:whitespace-nowrap">
                                                {t('mcqPlayerCorrectIndicator')}
                                            </span>
                                        )}
                                </div>
                            </div>

                            {/* Choice Feedback */}
                            {choiceFeedback && (
                                <div
                                    className={cn(
                                        'mcq-player-choice-feedback mt-1 ml-8 text-sm p-2 rounded',
                                        isCorrectChoice
                                            ? 'bg-success/10 text-success'
                                            : 'bg-destructive/10 text-destructive'
                                    )}
                                >
                                    {choiceFeedback}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Explanation (after grading) */}
            {showFeedback && result && revealCorrect && explanation && (
                <div className="mcq-player-explanation mt-4 p-4 rounded-lg bg-info/10 lg:w-full lg:max-w-4xl">
                    <h4 className="mcq-player-explanation-title font-medium mb-1">{t('explanation')}</h4>
                    <p className="mcq-player-explanation-body text-sm text-muted-foreground">
                        {explanation}
                    </p>
                </div>
            )}
        </div>
    );
};

export default MCQPlayer;
